import type { CSSProperties, ComponentType, FC } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { InfoCard } from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { cloudbuildApiRef } from '@backstage-community/plugin-cloudbuild';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import LaunchIcon from '@material-ui/icons/Launch';
import CodeIcon from '@material-ui/icons/Code';
import WebIcon from '@material-ui/icons/Web';
import SettingsIcon from '@material-ui/icons/Settings';
import CloudDoneIcon from '@material-ui/icons/CloudDone';
import useAsync from 'react-use/esm/useAsync';
import { BRAND_PRIMARY, BRAND_ACCENT, NEUTRAL, STATUS } from '../../theme/brand';

/**
 * Best-effort human name for a Cloud Build run.
 *
 * Cloud Build does not expose a stable "service" field, so this walks a
 * priority chain: explicit substitutions first, then the destination Artifact
 * Registry image path, then tags, then the build id.
 */
function extractTargetService(build: any): { name: string; idSnippet: string } {
  const idSnippet = build.id ? build.id.substring(0, 8) : '';

  if (build.substitutions?.REPO_NAME) {
    return { name: build.substitutions.REPO_NAME, idSnippet };
  }
  if (build.substitutions?.TRIGGER_NAME) {
    return { name: build.substitutions.TRIGGER_NAME, idSnippet };
  }

  if (build.images && build.images.length > 0) {
    const match = build.images[0].match(
      /docker\.pkg\.dev\/[^/]+\/([^/]+)(?:\/([^/:]+))?/,
    );
    if (match) {
      if (match[2] && match[2] !== 'app') return { name: match[2], idSnippet };
      if (match[1] && match[1] !== 'app') return { name: match[1], idSnippet };
    }
  }

  if (build.tags && build.tags.length > 0) {
    return { name: build.tags[0], idSnippet };
  }
  return { name: `Build #${idSnippet}`, idSnippet };
}

/** Pick the most "launchable" link off an entity, if any. */
function primaryLink(entity: any): string | undefined {
  const links = entity?.metadata?.links ?? [];
  const preferred = links.find(
    (l: any) =>
      l.icon === 'web' ||
      l.title?.toLowerCase().includes('production') ||
      l.title?.toLowerCase().includes('app'),
  );
  return (preferred ?? links[0])?.url;
}

const cardStyle: CSSProperties = {
  borderRadius: 12,
  border: `1px solid ${NEUTRAL.border}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const MetricCard = ({
  label,
  value,
  loading,
  color,
  icon: Icon,
}: {
  label: string;
  value: number;
  loading: boolean;
  color: string;
  icon: ComponentType<{ style?: CSSProperties }>;
}) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card style={cardStyle}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="body2"
              style={{
                color: NEUTRAL.muted,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="h3"
              style={{ fontWeight: 700, color, marginTop: 4 }}
            >
              {loading ? <CircularProgress size={28} /> : value}
            </Typography>
          </Box>
          <Icon style={{ fontSize: 42, color, opacity: 0.8 }} />
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

/**
 * Cloud Build panel body.
 *
 * Split out of the dashboard so each state is an early return rather than a
 * chain of nested ternaries.
 */
const CloudBuildPanelBody: FC<{
  projectId?: string;
  location: string;
  loading: boolean;
  builds: any[];
}> = ({ projectId, location, loading, builds }) => {
  if (!projectId) {
    return (
      <Box p={2}>
        <Typography variant="body2" style={{ color: NEUTRAL.muted }}>
          Cloud Build is not configured. Set{' '}
          <code>atteniv.cloudbuild.projectId</code> in app-config to stream
          recent builds here.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (builds.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" style={{ color: NEUTRAL.muted }}>
          No recent builds found for <strong>{projectId}</strong> in {location}.
        </Typography>
      </Box>
    );
  }

  const successfulBuilds = builds.filter(b => b.status === 'SUCCESS').length;

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="body2" style={{ color: NEUTRAL.body }}>
          Project: <strong>{projectId}</strong>
        </Typography>
        <Typography
          variant="body2"
          style={{ color: STATUS.successFg, fontWeight: 600 }}
        >
          {successfulBuilds}/{builds.length} Success
        </Typography>
      </Box>
      {builds.slice(0, 6).map(build => {
        const target = extractTargetService(build);
        const ok = build.status === 'SUCCESS';
        return (
          <Box
            key={build.id}
            py={1.5}
            style={{
              borderBottom: `1px solid ${NEUTRAL.borderSoft}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                variant="body2"
                style={{ fontWeight: 700, color: NEUTRAL.heading }}
              >
                {target.name}
              </Typography>
              <Typography variant="caption" style={{ color: NEUTRAL.faint }}>
                Build {target.idSnippet} •{' '}
                {new Date(build.createTime).toLocaleDateString()}
              </Typography>
            </Box>
            <Box
              px={1.5}
              py={0.5}
              style={{
                borderRadius: 12,
                fontSize: '0.75rem',
                fontWeight: 700,
                backgroundColor: ok ? STATUS.successBg : STATUS.dangerBg,
                color: ok ? STATUS.successFg : STATUS.dangerFg,
              }}
            >
              {build.status}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const AttenivDashboard: FC = () => {
  const catalogApi = useApi(catalogApiRef);
  const cloudbuildApi = useApi(cloudbuildApiRef);
  const configApi = useApi(configApiRef);

  // Cloud Build target is configuration, not code. Absent config, the panel
  // degrades to an explanatory message rather than querying a wrong project.
  const cloudbuildProjectId = configApi.getOptionalString(
    'atteniv.cloudbuild.projectId',
  );
  const cloudbuildLocation =
    configApi.getOptionalString('atteniv.cloudbuild.location') ?? 'us-west1';

  const catalogState = useAsync(async () => {
    const res = await catalogApi.getEntities({
      filter: { kind: ['Component', 'API', 'Template'] },
    });
    return res.items;
  }, [catalogApi]);

  const buildState = useAsync(async () => {
    if (!cloudbuildProjectId) return [];
    try {
      const res = await cloudbuildApi.listWorkflowRuns({
        projectId: cloudbuildProjectId,
        location: cloudbuildLocation,
        cloudBuildFilter: '',
      });
      return res.builds || [];
    } catch {
      return [];
    }
  }, [cloudbuildApi, cloudbuildProjectId, cloudbuildLocation]);

  const entities = catalogState.value || [];
  const webapps = entities.filter(
    e => e.kind === 'Component' && e.spec?.type === 'website',
  );
  const services = entities.filter(
    e => e.kind === 'Component' && e.spec?.type === 'service',
  );
  const apis = entities.filter(e => e.kind === 'API');
  const builds = buildState.value || [];

  return (
    <Box p={3} style={{ maxWidth: 1400, margin: '0 auto' }}>
      <Box mb={4}>
        <Typography variant="h4" style={{ fontWeight: 700, color: BRAND_PRIMARY }}>
          Atteniv Developer Portal
        </Typography>
        <Typography
          variant="subtitle1"
          style={{ color: NEUTRAL.muted, marginTop: 4 }}
        >
          Service catalog, ownership, and cloud deployments for Atteniv
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <MetricCard
          label="Production Webapps"
          value={webapps.length}
          loading={catalogState.loading}
          color={BRAND_PRIMARY}
          icon={WebIcon}
        />
        <MetricCard
          label="Backend Services"
          value={services.length}
          loading={catalogState.loading}
          color={BRAND_PRIMARY}
          icon={SettingsIcon}
        />
        <MetricCard
          label="Company APIs"
          value={apis.length}
          loading={catalogState.loading}
          color={BRAND_ACCENT}
          icon={CodeIcon}
        />
        <MetricCard
          label="Cloud Builds Tracked"
          value={builds.length}
          loading={buildState.loading}
          color={STATUS.successFg}
          icon={CloudDoneIcon}
        />
      </Grid>

      <Grid container spacing={3} style={{ marginTop: 16 }}>
        <Grid item xs={12} md={7}>
          <InfoCard
            title="Quick Launch Web Applications"
            subheader="Active Atteniv internal production applications"
          >
            <Grid container spacing={2}>
              {webapps.map(item => {
                const targetUrl = primaryLink(item);
                return (
                  <Grid item xs={12} sm={6} key={item.metadata.name}>
                    <Box
                      p={2}
                      style={{
                        borderRadius: 8,
                        border: `1px solid ${NEUTRAL.borderSoft}`,
                        backgroundColor: NEUTRAL.surface,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 700, color: NEUTRAL.heading }}
                        >
                          {item.metadata.title || item.metadata.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{
                            color: NEUTRAL.muted,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            marginTop: 4,
                          }}
                        >
                          {item.metadata.description ||
                            'No description provided'}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        {targetUrl ? (
                          <a
                            href={targetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              color: BRAND_PRIMARY,
                              fontWeight: 600,
                              fontSize: '0.8125rem',
                              textDecoration: 'none',
                            }}
                          >
                            <LaunchIcon style={{ fontSize: 16 }} />
                            {targetUrl
                              .replace(/^https?:\/\//, '')
                              .replace(/\/$/, '')}
                          </a>
                        ) : (
                          <Typography
                            variant="caption"
                            style={{ color: NEUTRAL.faint }}
                          >
                            Internal Service Only
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </InfoCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <InfoCard
            title="Google Cloud Build Status"
            subheader="Recent container builds"
          >
            <CloudBuildPanelBody
              projectId={cloudbuildProjectId}
              location={cloudbuildLocation}
              loading={buildState.loading}
              builds={builds}
            />
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
};

const dashboardWidget = HomePageWidgetBlueprint.make({
  name: 'atteniv-dashboard',
  params: {
    name: 'AttenivDashboard',
    title: 'Atteniv Portal Overview',
    description: 'Operational metrics and application launcher',
    components: async () => ({
      Content: () => <AttenivDashboard />,
    }),
  },
});

export const homeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [dashboardWidget],
});
